import { useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Coins } from "lucide-react";

const GOLD = "#C8A15C", VIOLET = "#B0863C";
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const DistributionDialog = ({ reserve, onSaved }) => {
  const [open, setOpen] = useState(false);
  const quarters = reserve?.quarters || [];
  const principals = reserve?.principals || [];
  const [period, setPeriod] = useState(quarters[0]?.period || "");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const selQ = useMemo(() => quarters.find((q) => q.period === period), [quarters, period]);
  const per = principals.length ? Number(amount || 0) / principals.length : 0;
  const overDistributable = selQ && Number(amount || 0) > selQ.remaining;

  const save = async () => {
    if (!period) { toast.error("Pick a quarter"); return; }
    if (!(Number(amount) > 0)) { toast.error("Enter an amount greater than 0"); return; }
    if (!reason.trim()) { toast.error("A reason is required (audited)"); return; }
    setSaving(true);
    try {
      await api.post("/admin/distributions", { period, period_type: "quarter", amount: Number(amount), reason });
      toast.success("Distribution declared");
      setOpen(false); setAmount(""); setReason(""); onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not declare distribution");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setPeriod(quarters[0]?.period || ""); }}>
      <DialogTrigger asChild>
        <Button data-testid="declare-distribution-btn" size="sm" className="rounded-full font-semibold" style={{ background: VIOLET }}>
          <Coins className="w-3.5 h-3.5 mr-1.5" /> Declare distribution
        </Button>
      </DialogTrigger>
      <DialogContent className="border-[#222430] max-w-lg" style={{ background: "#14151C" }}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">Declare owner distribution</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Discretionary quarterly profit distribution to the two principal partners. Not guaranteed; split equally and recorded with an audit trail.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <label className="label-mono text-[#71717A]">Quarter</label>
            <select data-testid="distribution-period-select" value={period} onChange={(e) => setPeriod(e.target.value)}
              className="mt-1.5 w-full bg-black/30 border border-[#222430] text-white rounded-md px-3 py-2 text-sm">
              {quarters.length ? quarters.map((q) => <option key={q.period} value={q.period}>{q.period}</option>)
                : <option value="">No revenue periods yet</option>}
            </select>
          </div>
          {selQ && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-black/30 rounded-lg px-3 py-2"><p className="label-mono text-[#71717A]">Distributable</p><p className="font-display font-bold" style={{ color: GOLD }}>{money(selQ.distributable)}</p></div>
              <div className="bg-black/30 rounded-lg px-3 py-2"><p className="label-mono text-[#71717A]">Remaining</p><p className="font-display font-bold text-white">{money(selQ.remaining)}</p></div>
            </div>
          )}
          <div>
            <label className="label-mono text-[#71717A]">Amount to distribute (USD)</label>
            <Input data-testid="distribution-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="mt-1.5 bg-black/30 border-[#222430] text-white" />
            {overDistributable && <p className="text-xs mt-1" style={{ color: "#FF5500" }}>Exceeds remaining distributable — allowed but discretionary.</p>}
          </div>
          <div className="bg-black/20 rounded-lg px-3 py-2">
            <p className="label-mono text-[#71717A] mb-1.5">Split equally among principals</p>
            {principals.map((p) => (
              <div key={p} className="flex justify-between text-sm py-0.5">
                <span className="text-[#E4E4E7] truncate">{p}</span>
                <span className="font-display font-semibold text-white">{money(per)}</span>
              </div>
            ))}
          </div>
          <div>
            <label className="label-mono text-[#71717A]">Reason (audited)</label>
            <Textarea data-testid="distribution-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Board-approved Q profit share…" className="mt-1.5 bg-black/30 border-[#222430] text-white resize-none" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="distribution-save-btn" onClick={save} disabled={saving} className="rounded-full font-semibold" style={{ background: VIOLET }}>
            {saving ? "Saving…" : "Declare distribution"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
